import type { HTMLAttributes } from 'react'
import { mergeClassNames } from '@/design-system/lib/mergeClassNames'
import {
  alertStatusTone,
  businessStatusTone,
  financialStatusTone,
  markerStatusTone,
  type AlertStatusValue,
  type BusinessStatusValue,
  type FinancialStatusValue,
  type MarkerStatusValue,
} from './statusFamilies'

// `pending` (violet) ajouté le 16 sept 2026 (audit UX/UI, Phase 1, §6.3) pour
// distinguer « Envoyé / attente de signature » de « Paiement requis /
// échéance » — les deux se confondaient auparavant en `warning`.
export type StatusTone =
  | 'success'
  | 'info'
  | 'warning'
  | 'danger'
  | 'neutral'
  | 'pending'
  | 'dangerLight'
  | 'neutralLight'
  | 'neutralStrong'

interface StatusBadgeBaseProps extends HTMLAttributes<HTMLSpanElement> {
  dot?: boolean
}

/**
 * Une page décrit ce qu'elle affiche — un statut métier, un état financier, une
 * alerte — et jamais la couleur qui va avec. `tone` reste accepté pour les
 * appels non encore migrés; il disparaîtra avec eux.
 */
type StatusBadgeFamilyProps =
  | { kind: 'business'; value: BusinessStatusValue; tone?: never }
  | { kind: 'financial'; value: FinancialStatusValue; tone?: never }
  | { kind: 'alert'; value: AlertStatusValue; tone?: never }
  | { kind: 'marker'; value: MarkerStatusValue; tone?: never }
  | { kind?: never; value?: never; tone?: StatusTone }

export type StatusBadgeProps = StatusBadgeBaseProps & StatusBadgeFamilyProps

function resolveTone(props: StatusBadgeFamilyProps): StatusTone {
  if (props.kind === 'business') return businessStatusTone[props.value]
  if (props.kind === 'financial') return financialStatusTone[props.value]
  if (props.kind === 'alert') return alertStatusTone[props.value]
  if (props.kind === 'marker') return markerStatusTone[props.value]
  return props.tone ?? 'neutral'
}

export function StatusBadge({ children, className, dot = true, ...props }: StatusBadgeProps) {
  const tone = resolveTone(props)
  // `kind`, `value` et `tone` décrivent le statut, pas le DOM : ils ne doivent
  // pas se retrouver en attributs sur le `span`.
  const { kind, value, tone: ignoredTone, ...rest } = props
  void kind
  void value
  void ignoredTone
  return (
    <span className={mergeClassNames('ds-status', `ds-status--${tone}`, className)} {...rest}>
      {dot ? <span className="ds-status__dot" aria-hidden="true" /> : null}
      {children}
    </span>
  )
}
